import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../Common/AppOverlay";
import SimpleBar from "simplebar-react";
import FeatherIcon from "feather-icons-react";
import {
  useNotifications,
  relativeTime,
} from "../../../contexts/NotificationContext";

const iconFor = (notification) => {
  if (notification.notificationType?.includes("Import"))
    return notification.severity === "Error"
      ? "x-circle"
      : notification.severity === "Warning"
      ? "alert-triangle"
      : "check-circle";
  if (notification.notificationType?.includes("Review")) return "file-text";
  if (notification.notificationType?.includes("Query")) return "message-circle";
  if (notification.notificationType?.includes("Finding")) return "flag";
  return "check-square";
};

const NotificationDropdown = () => {
  const [menu, setMenu] = useState(false);
  const { notifications, unreadCount, loading, refresh, markRead } =
    useNotifications();
  const navigate = useNavigate();
  useEffect(() => {
    if (menu) refresh();
  }, [menu, refresh]);

  const viewAll = () => {
    setMenu(false);
    navigate("/notifications");
  };

  const openNotification = async (notification) => {
    if (!notification.isRead) await markRead(notification.id);
    setMenu(false);
    navigate(notification.actionUrl || "/notifications");
  };

  return (
    <div className="d-inline-block">
      <button
        className="btn header-item noti-icon position-relative"
        type="button"
        aria-label="Notifications"
        data-tour="notifications"
        onClick={() => setMenu(true)}
      >
        <FeatherIcon icon="bell" className="icon-lg" />
        {unreadCount > 0 && (
          <span className="badge bg-danger rounded-pill">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      <Offcanvas
        className="notifications-drawer"
        direction="end"
        isOpen={menu}
        toggle={() => setMenu(false)}
      >
        <OffcanvasHeader toggle={() => setMenu(false)}>
          <span>Notifications</span>
        </OffcanvasHeader>
        <OffcanvasBody className="p-0 d-flex flex-column">
          <SimpleBar className="notifications-drawer__list flex-grow-1">
            {loading && (
              <div className="p-4 text-muted">Loading notifications…</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="p-4 text-center text-muted">
                <FeatherIcon
                  icon="check-circle"
                  size={32}
                  className="mb-3 text-success"
                />
                <h6>You’re all caught up</h6>
                <p className="mb-0">
                  New assignments, review activity and important updates will
                  appear here.
                </p>
              </div>
            )}
            {!loading &&
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  className={`text-reset notification-item notification-item--button ${
                    !notification.isRead ? "notification-item--unread" : ""
                  }`}
                  onClick={() => openNotification(notification)}
                >
                  <div className="d-flex">
                    <div
                      className={`avatar-sm me-3 notification-item__icon notification-item__icon--${
                        notification.severity?.toLowerCase() || "info"
                      }`}
                    >
                      <span className="avatar-title rounded-circle font-size-16">
                        <FeatherIcon icon={iconFor(notification)} size={17} />
                      </span>
                    </div>
                    <div className="flex-grow-1 text-start">
                      <h6 className="mt-0 mb-1">
                        {notification.title}
                        {!notification.isRead && (
                          <span className="notification-item__unread-dot" />
                        )}
                      </h6>
                      <div className="font-size-12 text-muted">
                        <p className="mb-1">{notification.message}</p>
                        <p className="mb-0">
                          <FeatherIcon
                            icon="clock"
                            size={12}
                            className="me-1"
                          />
                          {relativeTime(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
          </SimpleBar>
          <div className="notifications-drawer__footer">
            <button
              className="btn btn-sm btn-link font-size-14 btn-block text-center"
              type="button"
              onClick={viewAll}
            >
              <i className="mdi mdi-arrow-right-circle me-1" />
              View all notifications
            </button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
};

export default NotificationDropdown;
